import { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Select, MenuItem, FormControl, InputLabel,
  Chip, CircularProgress, Alert, Grid, Card, CardContent, Tabs, Tab,
  ToggleButtonGroup, ToggleButton, Autocomplete, Divider, IconButton,
  Tooltip, Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Inventory as StockInIcon,
  Medication as DispenseIcon,
  DeleteForever as DestroyIcon,
  Tune as AdjustIcon,
  EditNote as CorrectionIcon,
  VerifiedUser as VerifyIcon,
  Block as WasteIcon,
  Edit as EditIcon,
  Lock as LockIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { api } from '../services/apiClient';
import { API_ENDPOINTS } from '../config/api';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface LedgerEntry {
  transaction_id: string;
  medication_name: string;
  schedule_class: string;
  transaction_type: string;
  amount_changed: number;
  balance_after: number;
  pet_name: string | null;
  authorized_by_name: string;
  witnessed_by_name: string | null;
  notes: string | null;
  corrects_transaction_id: string | null;
  created_at: string;
  current_transaction_hash: string;
}

interface DrugBalance {
  medication_name: string;
  schedule_class: string;
  balance: number;
}

interface DrugCatalogEntry {
  id: number;
  drug_name: string;
  schedule_class: string;
  unit: string;
  concentration: string | null;
  supplier: string | null;
  notes: string | null;
  is_active: number;
}

interface Pet { id: number; name: string; }
interface StaffUser { id: number; full_name: string; }

// ─────────────────────────────────────────────────────────────────────────────
// Transaction type configuration
// ─────────────────────────────────────────────────────────────────────────────

const TX_CONFIG: Record<string, {
  label: string;
  isStockIn: boolean;
  showPatient: boolean;
  requiresWitness: boolean;
  requiresNotes: boolean;
  color: string;
  chipColor: 'success' | 'error' | 'warning' | 'info' | 'default';
}> = {
  RESTOCK_RECEIVED: { label: 'Stock Received',      isStockIn: true,  showPatient: false, requiresWitness: false, requiresNotes: false, color: '#2e7d32', chipColor: 'success' },
  ADMINISTERED:     { label: 'Administered',         isStockIn: false, showPatient: true,  requiresWitness: false, requiresNotes: false, color: '#c62828', chipColor: 'error'   },
  DISPENSED:        { label: 'Dispensed (Script)',   isStockIn: false, showPatient: true,  requiresWitness: false, requiresNotes: false, color: '#e91e63', chipColor: 'error'   },
  WASTED:           { label: 'Waste / Surplus',      isStockIn: false, showPatient: false, requiresWitness: true,  requiresNotes: true,  color: '#e65100', chipColor: 'warning' },
  DESTROYED:        { label: 'Destroyed / Disposed', isStockIn: false, showPatient: false, requiresWitness: true,  requiresNotes: true,  color: '#6a1b9a', chipColor: 'warning' },
  RECONCILIATION:   { label: 'Reconciliation',       isStockIn: false, showPatient: false, requiresWitness: true,  requiresNotes: true,  color: '#1565c0', chipColor: 'info'    },
  CORRECTION:       { label: 'Correction',           isStockIn: false, showPatient: false, requiresWitness: true,  requiresNotes: true,  color: '#795548', chipColor: 'default' },
};

const SCHEDULE_CLASSES = ['S4', 'S8'];
const UNITS = ['ml', 'mg', 'tablet', 'vial', 'ampoule', 'patch', 'suppository'];

const emptyForm = {
  drug_catalog_id: '' as string | number,
  medication_name: '',
  schedule_class: 'S8',
  transaction_type: 'RESTOCK_RECEIVED',
  amount_administered: '',
  amount_wasted: '',
  amount: '',
  batch_number: '',
  expiry_date: '',
  pet_id: '',
  witnessed_by_user_id: '',
  notes: '',
  authorization_pin: '',
  witness_pin: '',
};

const emptyCatalogForm = {
  drug_name: '',
  schedule_class: 'S8',
  unit: 'ml',
  concentration: '',
  supplier: '',
  notes: '',
};

const ControlledDrugsPage = () => {
  const [tab, setTab] = useState(0);
  const [entries, setEntries] = useState<LedgerEntry[]>([]);
  const [balances, setBalances] = useState<DrugBalance[]>([]);
  const [medications, setMedications] = useState<string[]>([]);
  const [catalog, setCatalog] = useState<DrugCatalogEntry[]>([]);
  const [pets, setPets] = useState<Pet[]>([]);
  const [staff, setStaff] = useState<StaffUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [correctionDialogOpen, setCorrectionDialogOpen] = useState(false);
  const [correctionTarget, setCorrectionTarget] = useState<LedgerEntry | null>(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [correctionForm, setCorrectionForm] = useState({
    correction_amount: '',
    witnessed_by_user_id: '',
    notes: '',
    authorization_pin: '',
    witness_pin: '',
  });

  // Catalog management state
  const [catalogDialogOpen, setCatalogDialogOpen] = useState(false);
  const [catalogEditTarget, setCatalogEditTarget] = useState<DrugCatalogEntry | null>(null);
  const [catalogForm, setCatalogForm] = useState({ ...emptyCatalogForm });

  // PIN setup state
  const [pinSetupOpen, setPinSetupOpen] = useState(false);
  const [pinSetupForm, setPinSetupForm] = useState({ pin: '', confirm: '' });
  const [pinSetupError, setPinSetupError] = useState('');
  const [pinNotSet, setPinNotSet] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [entriesRes, balancesRes, medsRes, petsRes, staffRes, catalogRes] = await Promise.all([
        api.get<{ data: LedgerEntry[] }>(API_ENDPOINTS.CONTROLLED_DRUGS.LIST),
        api.get<{ data: DrugBalance[] }>(API_ENDPOINTS.CONTROLLED_DRUGS.BALANCE),
        api.get<{ data: { medication_name: string }[] }>(API_ENDPOINTS.CONTROLLED_DRUGS.MEDICATIONS),
        api.get<{ data: Pet[] }>(API_ENDPOINTS.PATIENTS.LIST),
        api.get<{ data: StaffUser[] }>(API_ENDPOINTS.ADMIN.USERS),
        api.get<{ data: DrugCatalogEntry[] }>(API_ENDPOINTS.CONTROLLED_DRUGS.CATALOG_LIST),
      ]);
      setEntries(Array.isArray(entriesRes.data) ? entriesRes.data : []);
      setBalances(Array.isArray(balancesRes.data) ? balancesRes.data : []);
      setMedications((Array.isArray(medsRes.data) ? medsRes.data : []).map(m => m.medication_name));
      setPets(Array.isArray(petsRes.data) ? petsRes.data : []);
      setStaff(Array.isArray(staffRes.data) ? staffRes.data : []);
      setCatalog(Array.isArray(catalogRes.data) ? catalogRes.data : []);
    } catch {
      setError('Failed to load controlled drug register');
    } finally {
      setLoading(false);
    }
  };

  const txCfg = TX_CONFIG[formData.transaction_type] ?? TX_CONFIG.RESTOCK_RECEIVED;
  const isAdministered = formData.transaction_type === 'ADMINISTERED';
  const currentBalance = balances.find(b => b.medication_name === formData.medication_name)?.balance;
  const requiresWitnessPin = txCfg.requiresWitness || (isAdministered && !!formData.amount_wasted);

  // ── Create transaction ──────────────────────────────────────────────────────

  const handleCreate = async () => {
    if (!formData.medication_name) { setError('Medication name is required'); return; }

    if (isAdministered) {
      if (!formData.amount_administered && !formData.amount_wasted) {
        setError('Enter at least one amount (administered or wasted)');
        return;
      }
      if (formData.amount_wasted && !formData.witnessed_by_user_id) {
        setError('A witness is required when recording waste');
        return;
      }
    } else {
      if (!formData.amount) { setError('Amount is required'); return; }
      if (txCfg.requiresWitness && !formData.witnessed_by_user_id) {
        setError(`A witness is required for ${txCfg.label}`);
        return;
      }
      if (txCfg.requiresNotes && !formData.notes.trim()) {
        setError(`Notes are required for ${txCfg.label}`);
        return;
      }
    }

    try {
      setSaving(true);

      const pinFields = {
        ...(formData.authorization_pin ? { authorization_pin: formData.authorization_pin } : {}),
        ...(formData.witness_pin && requiresWitnessPin ? { witness_pin: formData.witness_pin } : {}),
      };

      if (isAdministered) {
        await api.post(API_ENDPOINTS.CONTROLLED_DRUGS.CREATE, {
          medication_name: formData.medication_name,
          schedule_class: formData.schedule_class,
          drug_catalog_id: formData.drug_catalog_id ? Number(formData.drug_catalog_id) : undefined,
          transaction_type: 'ADMINISTERED',
          amount_administered: formData.amount_administered ? parseFloat(formData.amount_administered) : undefined,
          amount_wasted: formData.amount_wasted ? parseFloat(formData.amount_wasted) : undefined,
          pet_id: formData.pet_id ? parseInt(formData.pet_id) : undefined,
          batch_number: formData.batch_number || undefined,
          expiry_date: formData.expiry_date || undefined,
          witnessed_by_user_id: formData.witnessed_by_user_id ? parseInt(formData.witnessed_by_user_id) : undefined,
          notes: formData.notes || undefined,
          ...pinFields,
        });
      } else {
        await api.post(API_ENDPOINTS.CONTROLLED_DRUGS.CREATE, {
          medication_name: formData.medication_name,
          schedule_class: formData.schedule_class,
          drug_catalog_id: formData.drug_catalog_id ? Number(formData.drug_catalog_id) : undefined,
          transaction_type: formData.transaction_type,
          amount: parseFloat(formData.amount),
          batch_number: formData.batch_number || undefined,
          expiry_date: formData.expiry_date || undefined,
          witnessed_by_user_id: formData.witnessed_by_user_id ? parseInt(formData.witnessed_by_user_id) : undefined,
          notes: formData.notes || undefined,
          ...pinFields,
        });
      }

      setDialogOpen(false);
      setFormData({ ...emptyForm });
      fetchData();
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: { code?: string; message?: string } } } };
      const code = apiErr?.response?.data?.error?.code;
      const msg = apiErr?.response?.data?.error?.message;
      if (code === 'PIN_NOT_SET') {
        setPinNotSet(true);
        setError(msg ?? 'You must set an authorization PIN first');
      } else if (code === 'INVALID_PIN') {
        setError('Incorrect PIN — please try again');
      } else {
        setError('Failed to record transaction');
      }
    } finally {
      setSaving(false);
    }
  };

  // ── Submit correction ────────────────────────────────────────────────────────

  const handleCorrection = async () => {
    if (!correctionTarget) return;
    if (!correctionForm.notes.trim()) { setError('Notes are required for a correction'); return; }
    if (!correctionForm.witnessed_by_user_id) { setError('A witness is required for corrections'); return; }
    if (!correctionForm.correction_amount) { setError('Correction amount is required'); return; }

    try {
      setSaving(true);
      await api.post(API_ENDPOINTS.CONTROLLED_DRUGS.CORRECTION(correctionTarget.transaction_id), {
        correction_amount: parseFloat(correctionForm.correction_amount),
        witnessed_by_user_id: parseInt(correctionForm.witnessed_by_user_id),
        notes: correctionForm.notes,
        ...(correctionForm.authorization_pin ? { authorization_pin: correctionForm.authorization_pin } : {}),
        ...(correctionForm.witness_pin ? { witness_pin: correctionForm.witness_pin } : {}),
      });
      setCorrectionDialogOpen(false);
      setCorrectionTarget(null);
      setCorrectionForm({ correction_amount: '', witnessed_by_user_id: '', notes: '', authorization_pin: '', witness_pin: '' });
      fetchData();
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: { code?: string; message?: string } } } };
      const code = apiErr?.response?.data?.error?.code;
      if (code === 'PIN_NOT_SET') { setPinNotSet(true); setError('You must set an authorization PIN first'); }
      else if (code === 'INVALID_PIN') { setError('Incorrect PIN — please try again'); }
      else { setError('Failed to submit correction'); }
    } finally {
      setSaving(false);
    }
  };

  // ── Catalog handlers ─────────────────────────────────────────────────────────

  const openAddCatalog = () => {
    setCatalogEditTarget(null);
    setCatalogForm({ ...emptyCatalogForm });
    setCatalogDialogOpen(true);
  };

  const openEditCatalog = (entry: DrugCatalogEntry) => {
    setCatalogEditTarget(entry);
    setCatalogForm({
      drug_name: entry.drug_name,
      schedule_class: entry.schedule_class,
      unit: entry.unit,
      concentration: entry.concentration ?? '',
      supplier: entry.supplier ?? '',
      notes: entry.notes ?? '',
    });
    setCatalogDialogOpen(true);
  };

  const handleSaveCatalog = async () => {
    if (!catalogForm.drug_name.trim()) { setError('Drug name is required'); return; }
    try {
      setSaving(true);
      if (catalogEditTarget) {
        await api.put(API_ENDPOINTS.CONTROLLED_DRUGS.CATALOG_UPDATE(catalogEditTarget.id), catalogForm);
      } else {
        await api.post(API_ENDPOINTS.CONTROLLED_DRUGS.CATALOG_CREATE, catalogForm);
      }
      setCatalogDialogOpen(false);
      fetchData();
    } catch {
      setError('Failed to save catalog entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivateCatalog = async (entry: DrugCatalogEntry) => {
    try {
      await api.delete(API_ENDPOINTS.CONTROLLED_DRUGS.CATALOG_DELETE(entry.id));
      fetchData();
    } catch {
      setError('Failed to deactivate drug');
    }
  };

  // ── PIN setup handler ────────────────────────────────────────────────────────

  const handleSetupPin = async () => {
    setPinSetupError('');
    if (!/^\d{4,6}$/.test(pinSetupForm.pin)) { setPinSetupError('PIN must be 4–6 digits'); return; }
    if (pinSetupForm.pin !== pinSetupForm.confirm) { setPinSetupError('PINs do not match'); return; }
    try {
      setSaving(true);
      await api.post(API_ENDPOINTS.AUTH.SETUP_PIN, { pin: pinSetupForm.pin });
      setPinSetupOpen(false);
      setPinSetupForm({ pin: '', confirm: '' });
      setPinNotSet(false);
    } catch {
      setPinSetupError('Failed to save PIN');
    } finally {
      setSaving(false);
    }
  };

  // ── When a catalog drug is selected in the form ──────────────────────────────

  const handleCatalogSelect = (entry: DrugCatalogEntry | null) => {
    if (entry) {
      setFormData(prev => ({
        ...prev,
        drug_catalog_id: entry.id,
        medication_name: entry.drug_name,
        schedule_class: entry.schedule_class,
      }));
    } else {
      setFormData(prev => ({ ...prev, drug_catalog_id: '', medication_name: '' }));
    }
  };

  // ── Display helpers ──────────────────────────────────────────────────────────

  const balanceColor = (bal: number): 'success' | 'warning' | 'error' => {
    if (bal <= 0) return 'error';
    if (bal <= 10) return 'warning';
    return 'success';
  };

  const amountDisplay = (amount: number) => {
    if (amount > 0) return <span style={{ color: '#2e7d32', fontWeight: 700 }}>+{amount.toFixed(4)}</span>;
    return <span style={{ color: '#c62828', fontWeight: 700 }}>{amount.toFixed(4)}</span>;
  };

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
      <CircularProgress />
    </Box>
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Controlled Drug Register</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Append-only audit ledger — every entry is permanent and cryptographically chained.
        Mistakes are corrected by adding a new Correction entry, never by editing.
      </Typography>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── PIN not set warning banner ──────────────────────────────────────── */}
      {pinNotSet && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          icon={<WarningIcon />}
          action={
            <Button size="small" startIcon={<LockIcon />} onClick={() => setPinSetupOpen(true)}>
              Set PIN
            </Button>
          }
        >
          <strong>Authorization PIN required.</strong> You must set a 4–6 digit PIN before logging controlled drug transactions.
        </Alert>
      )}

      {/* ── Summary cards ──────────────────────────────────────────────────── */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2">Total Entries</Typography>
              <Typography variant="h4">{entries.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2">Medications Tracked</Typography>
              <Typography variant="h4">{balances.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2">Zero / Negative Balance</Typography>
              <Typography variant="h4" color="error.main">
                {balances.filter(b => b.balance <= 0).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2">Corrections</Typography>
              <Typography variant="h4" color="warning.main">
                {entries.filter(e => e.transaction_type === 'CORRECTION').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          Record Transaction
        </Button>
        <Button variant="outlined" startIcon={<LockIcon />} onClick={() => setPinSetupOpen(true)}>
          {pinNotSet ? 'Set Authorization PIN' : 'Change Authorization PIN'}
        </Button>
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`Ledger (${entries.length})`} />
        <Tab label={`Balances (${balances.length})`} />
        <Tab label={`Drug Catalog (${catalog.length})`} />
      </Tabs>

      {/* ── Ledger tab ──────────────────────────────────────────────────────── */}
      {tab === 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date / Time</TableCell>
                <TableCell>Medication</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">Balance</TableCell>
                <TableCell>Patient</TableCell>
                <TableCell>Authorised By</TableCell>
                <TableCell>Witnessed By</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell sx={{ width: 40 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map(e => {
                const cfg = TX_CONFIG[e.transaction_type];
                return (
                  <TableRow
                    key={e.transaction_id}
                    hover
                    sx={e.transaction_type === 'CORRECTION' ? { bgcolor: 'action.hover' } : undefined}
                  >
                    <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                      {new Date(e.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell><strong>{e.medication_name}</strong></TableCell>
                    <TableCell><Chip label={e.schedule_class} size="small" /></TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Chip
                          label={cfg?.label ?? e.transaction_type}
                          size="small"
                          color={cfg?.chipColor ?? 'default'}
                        />
                        {e.corrects_transaction_id && (
                          <Tooltip title={`Corrects: ${e.corrects_transaction_id.slice(0, 8)}…`}>
                            <Chip label="REF" size="small" variant="outlined" />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{amountDisplay(e.amount_changed)}</TableCell>
                    <TableCell align="right">
                      <Chip label={e.balance_after.toFixed(2)} size="small" color={balanceColor(e.balance_after)} />
                    </TableCell>
                    <TableCell>{e.pet_name ?? '—'}</TableCell>
                    <TableCell>{e.authorized_by_name ?? '—'}</TableCell>
                    <TableCell>{e.witnessed_by_name ?? '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Tooltip title={e.notes ?? ''}>
                        <span>{e.notes ?? '—'}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {e.transaction_type !== 'CORRECTION' && (
                        <Tooltip title="Submit correction for this entry">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setCorrectionTarget(e);
                              setCorrectionDialogOpen(true);
                            }}
                          >
                            <CorrectionIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {entries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} align="center">No ledger entries yet</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Balances tab ────────────────────────────────────────────────────── */}
      {tab === 1 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Medication</TableCell>
                <TableCell>Schedule</TableCell>
                <TableCell align="right">Current Balance</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Chain</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {balances.map(b => (
                <TableRow key={b.medication_name} hover>
                  <TableCell><strong>{b.medication_name}</strong></TableCell>
                  <TableCell><Chip label={b.schedule_class} size="small" /></TableCell>
                  <TableCell align="right">{b.balance.toFixed(4)}</TableCell>
                  <TableCell>
                    <Chip
                      label={b.balance <= 0 ? 'Empty' : b.balance <= 10 ? 'Low' : 'OK'}
                      color={balanceColor(b.balance)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Verify cryptographic chain integrity">
                      <IconButton
                        size="small"
                        href={API_ENDPOINTS.CONTROLLED_DRUGS.VERIFY(b.medication_name)}
                        target="_blank"
                      >
                        <VerifyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {balances.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">No balances recorded yet</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Drug Catalog tab ────────────────────────────────────────────────── */}
      {tab === 2 && (
        <Box>
          <Box sx={{ mb: 2 }}>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={openAddCatalog}>
              Add Drug to Catalog
            </Button>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Drug Name</TableCell>
                  <TableCell>Schedule</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell>Concentration</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell sx={{ width: 80 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {catalog.map(entry => (
                  <TableRow key={entry.id} hover sx={!entry.is_active ? { opacity: 0.5 } : undefined}>
                    <TableCell><strong>{entry.drug_name}</strong></TableCell>
                    <TableCell>
                      <Chip
                        label={entry.schedule_class}
                        size="small"
                        color={entry.schedule_class === 'S8' ? 'error' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>{entry.unit}</TableCell>
                    <TableCell>{entry.concentration ?? '—'}</TableCell>
                    <TableCell>{entry.supplier ?? '—'}</TableCell>
                    <TableCell>
                      <Chip
                        label={entry.is_active ? 'Active' : 'Inactive'}
                        size="small"
                        color={entry.is_active ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEditCatalog(entry)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {entry.is_active ? (
                          <Tooltip title="Deactivate">
                            <IconButton size="small" onClick={() => handleDeactivateCatalog(entry)}>
                              <WasteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {catalog.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No drugs in catalog yet. Add drugs to enable standardised selection in transactions.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* ── Record Transaction Dialog ─────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Controlled Drug Transaction</DialogTitle>
        <DialogContent>

          {/* Step 1: Transaction type */}
          <Typography variant="overline" color="text.secondary">Transaction type</Typography>
          <ToggleButtonGroup
            exclusive fullWidth size="small"
            value={formData.transaction_type}
            onChange={(_, val) => val && setFormData({ ...emptyForm, transaction_type: val })}
            sx={{ mt: 0.5, mb: 2, flexWrap: 'wrap', gap: 0.5 }}
          >
            <ToggleButton value="RESTOCK_RECEIVED" sx={{ flexDirection: 'column', py: 1, gap: 0.5, flex: '1 0 auto' }}>
              <StockInIcon fontSize="small" />
              <Typography variant="caption">Stock In</Typography>
            </ToggleButton>
            <ToggleButton value="ADMINISTERED" sx={{ flexDirection: 'column', py: 1, gap: 0.5, flex: '1 0 auto' }}>
              <DispenseIcon fontSize="small" />
              <Typography variant="caption">Administer</Typography>
            </ToggleButton>
            <ToggleButton value="DISPENSED" sx={{ flexDirection: 'column', py: 1, gap: 0.5, flex: '1 0 auto' }}>
              <DispenseIcon fontSize="small" />
              <Typography variant="caption">Dispense</Typography>
            </ToggleButton>
            <ToggleButton value="WASTED" sx={{ flexDirection: 'column', py: 1, gap: 0.5, flex: '1 0 auto' }}>
              <WasteIcon fontSize="small" />
              <Typography variant="caption">Waste</Typography>
            </ToggleButton>
            <ToggleButton value="DESTROYED" sx={{ flexDirection: 'column', py: 1, gap: 0.5, flex: '1 0 auto' }}>
              <DestroyIcon fontSize="small" />
              <Typography variant="caption">Destroy</Typography>
            </ToggleButton>
            <ToggleButton value="RECONCILIATION" sx={{ flexDirection: 'column', py: 1, gap: 0.5, flex: '1 0 auto' }}>
              <AdjustIcon fontSize="small" />
              <Typography variant="caption">Reconcile</Typography>
            </ToggleButton>
          </ToggleButtonGroup>

          <Divider sx={{ mb: 2 }} />

          {/* Step 2: Medication — from catalog or free-text */}
          <Typography variant="overline" color="text.secondary">Medication</Typography>
          {catalog.length > 0 ? (
            <Box mt={0.5} mb={2}>
              <Autocomplete
                options={catalog}
                getOptionLabel={o => o.drug_name}
                value={catalog.find(c => c.id === formData.drug_catalog_id) ?? null}
                onChange={(_, val) => handleCatalogSelect(val)}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip label={option.schedule_class} size="small" color={option.schedule_class === 'S8' ? 'error' : 'warning'} />
                      <span>{option.drug_name}</span>
                      {option.concentration && <Typography variant="caption" color="text.secondary">{option.concentration}</Typography>}
                    </Stack>
                  </Box>
                )}
                renderInput={params => (
                  <TextField {...params} label="Select from Drug Catalog *" size="small" />
                )}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Drug not in catalog?{' '}
                <Button
                  size="small"
                  variant="text"
                  sx={{ p: 0, minWidth: 0, textDecoration: 'underline' }}
                  onClick={() => {
                    setDialogOpen(false);
                    setCatalogDialogOpen(true);
                    setTab(2);
                  }}
                >
                  Add it to the catalog first
                </Button>
              </Typography>
              {formData.drug_catalog_id && (
                <Box display="flex" gap={1} mt={1}>
                  <Chip label={formData.schedule_class} size="small" />
                  <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                    Schedule class auto-filled from catalog
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box display="flex" gap={2} mt={0.5} mb={2} alignItems="flex-start">
              <Autocomplete
                freeSolo
                options={medications}
                value={formData.medication_name}
                onInputChange={(_, val) => setFormData({ ...formData, medication_name: val })}
                renderInput={params => <TextField {...params} label="Medication Name *" size="small" />}
                sx={{ flex: 1 }}
              />
              <FormControl size="small" sx={{ width: 100 }}>
                <InputLabel>Class</InputLabel>
                <Select value={formData.schedule_class} label="Class"
                  onChange={e => setFormData({ ...formData, schedule_class: e.target.value })}>
                  {SCHEDULE_CLASSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Current balance hint */}
          {formData.medication_name && currentBalance !== undefined && (
            <Alert
              severity={currentBalance <= 0 ? 'error' : currentBalance <= 10 ? 'warning' : 'info'}
              sx={{ mb: 2, py: 0.5 }}
            >
              Current balance for <strong>{formData.medication_name}</strong>: <strong>{currentBalance.toFixed(4)}</strong>
            </Alert>
          )}

          {/* Step 3: Quantity */}
          <Typography variant="overline" color="text.secondary">
            {isAdministered ? 'Amounts' : 'Amount'}
          </Typography>

          {isAdministered ? (
            <Box display="flex" gap={2} mt={0.5} mb={2}>
              <TextField
                label="Amount Administered" type="number" size="small" sx={{ flex: 1 }}
                inputProps={{ min: 0, step: 0.0001 }}
                value={formData.amount_administered}
                onChange={e => setFormData({ ...formData, amount_administered: e.target.value })}
                InputProps={{ startAdornment: <Box component="span" sx={{ mr: 1, color: 'error.main', fontWeight: 700 }}>−</Box> }}
              />
              <TextField
                label="Amount Wasted" type="number" size="small" sx={{ flex: 1 }}
                inputProps={{ min: 0, step: 0.0001 }}
                value={formData.amount_wasted}
                onChange={e => setFormData({ ...formData, amount_wasted: e.target.value })}
                helperText="Requires witness"
                InputProps={{ startAdornment: <Box component="span" sx={{ mr: 1, color: 'warning.main', fontWeight: 700 }}>−</Box> }}
              />
            </Box>
          ) : (
            <TextField
              fullWidth size="small" type="number" sx={{ mt: 0.5, mb: 2 }}
              label="Amount"
              inputProps={{ min: 0, step: 0.0001 }}
              value={formData.amount}
              onChange={e => setFormData({ ...formData, amount: e.target.value })}
              InputProps={{
                startAdornment: (
                  <Box component="span" sx={{ mr: 1, color: txCfg.isStockIn ? 'success.main' : 'error.main', fontWeight: 700, fontSize: 18 }}>
                    {txCfg.isStockIn ? '+' : '−'}
                  </Box>
                ),
              }}
            />
          )}

          {/* Patient — for ADMINISTERED and DISPENSED */}
          {txCfg.showPatient && (
            <>
              <Typography variant="overline" color="text.secondary">Patient</Typography>
              <FormControl fullWidth size="small" sx={{ mt: 0.5, mb: 2 }}>
                <InputLabel>Patient</InputLabel>
                <Select value={formData.pet_id} label="Patient"
                  onChange={e => setFormData({ ...formData, pet_id: String(e.target.value) })}>
                  <MenuItem value=""><em>Select patient…</em></MenuItem>
                  {pets.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                </Select>
              </FormControl>
            </>
          )}

          {/* Witness — required for WASTED, DESTROYED, RECONCILIATION; shown for ADMINISTERED when waste > 0 */}
          {(txCfg.requiresWitness || isAdministered) && (
            <>
              <Typography variant="overline" color="text.secondary">
                Witness {txCfg.requiresWitness ? '*' : '(required if waste recorded)'}
              </Typography>
              <FormControl fullWidth size="small" sx={{ mt: 0.5, mb: 2 }}>
                <InputLabel>Witnessed By</InputLabel>
                <Select value={formData.witnessed_by_user_id} label="Witnessed By"
                  onChange={e => setFormData({ ...formData, witnessed_by_user_id: String(e.target.value) })}>
                  <MenuItem value=""><em>Select witness…</em></MenuItem>
                  {staff.map(s => <MenuItem key={s.id} value={s.id}>{s.full_name}</MenuItem>)}
                </Select>
              </FormControl>
            </>
          )}

          <Divider sx={{ mb: 2 }} />

          {/* Extra details */}
          <Typography variant="overline" color="text.secondary">Details</Typography>
          <Box display="flex" gap={2} mt={0.5} mb={1}>
            <TextField label="Batch #" size="small" sx={{ flex: 1 }}
              value={formData.batch_number}
              onChange={e => setFormData({ ...formData, batch_number: e.target.value })} />
            <TextField label="Expiry" type="date" size="small" sx={{ flex: 1 }}
              InputLabelProps={{ shrink: true }} value={formData.expiry_date}
              onChange={e => setFormData({ ...formData, expiry_date: e.target.value })} />
          </Box>
          <TextField
            fullWidth size="small" label={txCfg.requiresNotes ? 'Notes / Reason *' : 'Notes / Reason'}
            multiline rows={2} sx={{ mb: 2 }} value={formData.notes}
            onChange={e => setFormData({ ...formData, notes: e.target.value })}
          />

          {txCfg.requiresNotes && (
            <Alert severity="warning" sx={{ mb: 2, py: 0.5 }}>
              Notes are mandatory for {txCfg.label} entries.
            </Alert>
          )}

          <Divider sx={{ mb: 2 }} />

          {/* Step: Authorization PIN */}
          <Typography variant="overline" color="text.secondary">Authorization PIN</Typography>
          <Alert severity="info" sx={{ mt: 0.5, mb: 1.5, py: 0.5 }} icon={<LockIcon fontSize="small" />}>
            Enter your authorization PIN to sign this transaction. If you haven't set one,{' '}
            <Button size="small" variant="text" sx={{ p: 0, minWidth: 0, textDecoration: 'underline' }} onClick={() => setPinSetupOpen(true)}>
              set it here
            </Button>.
          </Alert>
          <Box display="flex" gap={2}>
            <TextField
              label="Your PIN *" size="small" type="password"
              inputProps={{ inputMode: 'numeric', maxLength: 6 }}
              value={formData.authorization_pin}
              onChange={e => setFormData({ ...formData, authorization_pin: e.target.value })}
              sx={{ flex: 1 }}
            />
            {requiresWitnessPin && formData.witnessed_by_user_id && (
              <TextField
                label="Witness PIN *" size="small" type="password"
                inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                value={formData.witness_pin}
                onChange={e => setFormData({ ...formData, witness_pin: e.target.value })}
                sx={{ flex: 1 }}
                helperText="Witness must enter their PIN"
              />
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => { setDialogOpen(false); setFormData({ ...emptyForm }); }} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={saving || !formData.medication_name}
            sx={{ bgcolor: txCfg.color, '&:hover': { bgcolor: txCfg.color, filter: 'brightness(0.9)' } }}
          >
            {saving ? <CircularProgress size={20} /> : `Record ${txCfg.label}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Correction Dialog ───────────────────────────────────────────────────── */}
      <Dialog
        open={correctionDialogOpen}
        onClose={() => { setCorrectionDialogOpen(false); setCorrectionTarget(null); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CorrectionIcon color="warning" />
            Submit Correction
          </Box>
        </DialogTitle>
        <DialogContent>
          {correctionTarget && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Original entry:</strong><br />
              {new Date(correctionTarget.created_at).toLocaleString()} —{' '}
              {TX_CONFIG[correctionTarget.transaction_type]?.label}{' '}
              of <strong>{correctionTarget.amount_changed > 0 ? '+' : ''}{correctionTarget.amount_changed.toFixed(4)}</strong>{' '}
              ({correctionTarget.medication_name})<br />
              <Typography variant="caption" color="text.secondary">
                ID: {correctionTarget.transaction_id}
              </Typography>
            </Alert>
          )}

          <Alert severity="warning" sx={{ mb: 2 }}>
            This creates a new <strong>CORRECTION</strong> entry referencing the original.
            The original entry is permanent and cannot be deleted.
          </Alert>

          <TextField
            fullWidth size="small" type="number" label="Correction Amount *"
            helperText="Signed delta that restores the correct balance. E.g. if −5.0 was logged instead of −0.5, enter +4.5"
            value={correctionForm.correction_amount}
            onChange={e => setCorrectionForm({ ...correctionForm, correction_amount: e.target.value })}
            sx={{ mb: 2 }}
            inputProps={{ step: 0.0001 }}
          />

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Witnessed By *</InputLabel>
            <Select value={correctionForm.witnessed_by_user_id} label="Witnessed By *"
              onChange={e => setCorrectionForm({ ...correctionForm, witnessed_by_user_id: String(e.target.value) })}>
              <MenuItem value=""><em>Select witness…</em></MenuItem>
              {staff.map(s => <MenuItem key={s.id} value={s.id}>{s.full_name}</MenuItem>)}
            </Select>
          </FormControl>

          <TextField
            fullWidth size="small" label="Reason for Correction *"
            multiline rows={3}
            placeholder="Describe what was incorrect in the original entry and why this correction is needed…"
            value={correctionForm.notes}
            onChange={e => setCorrectionForm({ ...correctionForm, notes: e.target.value })}
            sx={{ mb: 2 }}
          />

          <Divider sx={{ mb: 2 }} />
          <Typography variant="overline" color="text.secondary">Authorization PINs</Typography>
          <Box display="flex" gap={2} mt={0.5}>
            <TextField
              label="Your PIN *" size="small" type="password"
              inputProps={{ inputMode: 'numeric', maxLength: 6 }}
              value={correctionForm.authorization_pin}
              onChange={e => setCorrectionForm({ ...correctionForm, authorization_pin: e.target.value })}
              sx={{ flex: 1 }}
            />
            {correctionForm.witnessed_by_user_id && (
              <TextField
                label="Witness PIN *" size="small" type="password"
                inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                value={correctionForm.witness_pin}
                onChange={e => setCorrectionForm({ ...correctionForm, witness_pin: e.target.value })}
                sx={{ flex: 1 }}
              />
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setCorrectionDialogOpen(false);
              setCorrectionTarget(null);
              setCorrectionForm({ correction_amount: '', witnessed_by_user_id: '', notes: '', authorization_pin: '', witness_pin: '' });
            }}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCorrection}
            variant="contained"
            color="warning"
            disabled={saving || !correctionForm.correction_amount || !correctionForm.notes.trim() || !correctionForm.witnessed_by_user_id}
          >
            {saving ? <CircularProgress size={20} /> : 'Submit Correction'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Drug Catalog Add / Edit Dialog ──────────────────────────────────── */}
      <Dialog open={catalogDialogOpen} onClose={() => setCatalogDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{catalogEditTarget ? 'Edit Drug' : 'Add Drug to Catalog'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }} display="flex" flexDirection="column" gap={2}>
            <TextField
              fullWidth size="small" label="Drug Name *"
              value={catalogForm.drug_name}
              onChange={e => setCatalogForm({ ...catalogForm, drug_name: e.target.value })}
            />
            <Box display="flex" gap={2}>
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>Schedule Class *</InputLabel>
                <Select value={catalogForm.schedule_class} label="Schedule Class *"
                  onChange={e => setCatalogForm({ ...catalogForm, schedule_class: e.target.value })}>
                  {SCHEDULE_CLASSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>Unit *</InputLabel>
                <Select value={catalogForm.unit} label="Unit *"
                  onChange={e => setCatalogForm({ ...catalogForm, unit: e.target.value })}>
                  {UNITS.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
            <TextField
              fullWidth size="small" label="Concentration (display only)"
              placeholder="e.g. 100 mg/ml"
              value={catalogForm.concentration}
              onChange={e => setCatalogForm({ ...catalogForm, concentration: e.target.value })}
            />
            <TextField
              fullWidth size="small" label="Supplier"
              value={catalogForm.supplier}
              onChange={e => setCatalogForm({ ...catalogForm, supplier: e.target.value })}
            />
            <TextField
              fullWidth size="small" label="Notes" multiline rows={2}
              value={catalogForm.notes}
              onChange={e => setCatalogForm({ ...catalogForm, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCatalogDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSaveCatalog} variant="contained" disabled={saving || !catalogForm.drug_name.trim()}>
            {saving ? <CircularProgress size={20} /> : (catalogEditTarget ? 'Save Changes' : 'Add to Catalog')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── PIN Setup Dialog ────────────────────────────────────────────────── */}
      <Dialog open={pinSetupOpen} onClose={() => { setPinSetupOpen(false); setPinSetupForm({ pin: '', confirm: '' }); setPinSetupError(''); }} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <LockIcon />
            Set Authorization PIN
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This PIN will be required to sign all controlled drug transactions.
            Keep it private — do not share with other staff members.
          </Typography>
          {pinSetupError && <Alert severity="error" sx={{ mb: 2 }}>{pinSetupError}</Alert>}
          <Box display="flex" flexDirection="column" gap={2} sx={{ pt: 0.5 }}>
            <TextField
              fullWidth size="small" label="New PIN *" type="password"
              inputProps={{ inputMode: 'numeric', maxLength: 6 }}
              helperText="4–6 digits"
              value={pinSetupForm.pin}
              onChange={e => setPinSetupForm({ ...pinSetupForm, pin: e.target.value })}
            />
            <TextField
              fullWidth size="small" label="Confirm PIN *" type="password"
              inputProps={{ inputMode: 'numeric', maxLength: 6 }}
              value={pinSetupForm.confirm}
              onChange={e => setPinSetupForm({ ...pinSetupForm, confirm: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPinSetupOpen(false); setPinSetupForm({ pin: '', confirm: '' }); setPinSetupError(''); }} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSetupPin}
            variant="contained"
            disabled={saving || !pinSetupForm.pin || !pinSetupForm.confirm}
          >
            {saving ? <CircularProgress size={20} /> : 'Save PIN'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ControlledDrugsPage;
